import React, {useEffect, useState, useCallback, useRef, useMemo} from "react";
import { useTranslation } from "react-i18next";
import {formatDate, formatTime} from "../../util/StringUtils";
import Button from "react-bootstrap/Button";
import {getLoggedUser, isAdmin} from "../../util/SecurityUtils";
import {useIsMobile} from "../../hooks/useIsMobile";
import {Alert} from "react-bootstrap";
import ConfirmModal from "../common/ConfirmModal";
import {useNavigate} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {useChatMessages, useMentionablePlayers, usePinnedMessages} from "../../hooks/queries/useChatMessages";
import {
  useSendMessage,
  useDeleteMessage,
  useAddReaction,
  useRemoveReaction,
  useEditMessage,
  usePinMessage,
} from "../../hooks/mutations/useChatMutations";
import {markChatVisited} from "../../hooks/useChatLastVisit";
import {useMentionAutocomplete} from "../../hooks/useMentionAutocomplete";
import {useChatRealtime} from "../../hooks/useChatRealtime";
import {useTypingIndicator} from "../../hooks/useTypingIndicator";
import ChatMessageBody from "./ChatMessageBody";
import ChatMessageReactions from "./ChatMessageReactions";
import ChatGifPicker from "./ChatGifPicker";
import {getUserColor} from "../../util/UserColor";
import type {ChatMention, ChatMessage} from "../../types/domain";
import EmojiPicker, {Theme as EmojiTheme, Categories as EmojiCategories} from "emoji-picker-react";
import {useTheme} from "../../context/ThemeContext";

const CZ_EMOJI_CATEGORIES = [
  { category: EmojiCategories.SUGGESTED, name: "Naposledy použité" },
  { category: EmojiCategories.SMILEYS_PEOPLE, name: "Smajlíci a lidé" },
  { category: EmojiCategories.ANIMALS_NATURE, name: "Zvířata a příroda" },
  { category: EmojiCategories.FOOD_DRINK, name: "Jídlo a pití" },
  { category: EmojiCategories.TRAVEL_PLACES, name: "Cestování a místa" },
  { category: EmojiCategories.ACTIVITIES, name: "Aktivity" },
  { category: EmojiCategories.OBJECTS, name: "Předměty" },
  { category: EmojiCategories.SYMBOLS, name: "Symboly" },
  { category: EmojiCategories.FLAGS, name: "Vlajky" },
];

const EDIT_WINDOW_MS = 60 * 1000;

interface ChatPanelProps {
  tournamentId?: number | null;
  showHeader?: boolean;
  asPage?: boolean;
}

function messageAuthorDisplayName(msg: ChatMessage): string {
  const a = msg.author;
  return a.nickname && a.nickname.length > 0 ? a.nickname : `${a.firstname} ${a.lastname}`;
}

function ChatPanel({ tournamentId = null, showHeader = true, asPage = true }: ChatPanelProps) {
  const { t, i18n } = useTranslation();
  const [newMessage, setNewMessage] = useState("");
  const [pendingMentions, setPendingMentions] = useState<ChatMention[]>([]);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingMentions, setEditingMentions] = useState<ChatMention[]>([]);
  const [error, setError] = useState("");
  const [showDeleteMessageConfirmModal, setShowDeleteMessageConfirmModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [pinnedCollapsed, setPinnedCollapsed] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const loggedUser = getLoggedUser();
  const canSend = !!loggedUser?.playerId;
  const currentUser = loggedUser?.id;
  const currentPlayerId = loggedUser?.playerId ?? null;

  const { data: mentionable = [] } = useMentionablePlayers(canSend);
  const { data: pinnedMessages = [] } = usePinnedMessages(tournamentId);

  useChatRealtime(tournamentId);
  const { typingNames, notifyTyping } = useTypingIndicator(tournamentId);

  const { handleChange: onInputChange, handleKeyDown: onInputKeyDown, overlay: mentionOverlay } =
    useMentionAutocomplete({
      value: newMessage,
      setValue: setNewMessage,
      inputRef,
      mentionable,
      pendingMentions,
      setPendingMentions,
    });

  const editAutocomplete = useMentionAutocomplete({
    value: editingText,
    setValue: setEditingText,
    inputRef: editInputRef,
    mentionable,
    pendingMentions: editingMentions,
    setPendingMentions: setEditingMentions,
  });

  useEffect(() => {
    if (!showEmojiPicker) return;
    const onClick = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showEmojiPicker]);

  useEffect(() => {
    // Tick every 30s to re-evaluate "can edit" (5-min window).
    const interval = window.setInterval(() => setNowTick(Date.now()), 5_000);
    return () => window.clearInterval(interval);
  }, []);

  const messagesPerPage = isMobile ? 6 : 10;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: queryError,
  } = useChatMessages(messagesPerPage, tournamentId);

  const sendMutation = useSendMessage(tournamentId);
  const deleteMutation = useDeleteMessage(isAdmin(), tournamentId);
  const editMutation = useEditMessage(tournamentId);
  const pinMutation = usePinMessage(tournamentId);
  const addReactionMutation = useAddReaction();
  const removeReactionMutation = useRemoveReaction();

  const messages: ChatMessage[] = data?.pages.flat() ?? [];
  const loading = isLoading || isFetchingNextPage;
  const sending = sendMutation.isPending;

  useEffect(() => {
    const hasClosedWarning = localStorage.getItem("hasClosedChatWarning") === "true";
    setShowWarning(!hasClosedWarning);
  }, []);

  useEffect(() => {
    markChatVisited(Date.now(), tournamentId);
    return () => markChatVisited(Date.now(), tournamentId);
  }, [tournamentId]);

  useEffect(() => {
    if (queryError) setError(t("failedToLoadMessages"));
    else setError("");
  }, [queryError, t]);

  const closeWarning = () => {
    setShowWarning(false);
    localStorage.setItem("hasClosedChatWarning", "true");
  };

  const linkToPlayer = useCallback((id: number) => navigate('/players/' + id), [navigate]);

  const resolveMentionIdsForSend = useCallback((): number[] => {
    const explicit = pendingMentions
      .filter(m => newMessage.includes(`@${m.name}`))
      .map(m => m.playerId);
    if (replyingTo && replyingTo.author.id != null && replyingTo.author.userId !== currentUser) {
      // Auto-mention the reply target (unless replying to self).
      if (!explicit.includes(replyingTo.author.id)) explicit.push(replyingTo.author.id);
    }
    return explicit;
  }, [pendingMentions, newMessage, replyingTo, currentUser]);

  const handleSend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      const mentionedPlayerIds = resolveMentionIdsForSend();
      await sendMutation.mutateAsync({
        content: newMessage,
        mentionedPlayerIds,
        replyToMessageId: replyingTo?.id ?? null,
      });
      setNewMessage("");
      setPendingMentions([]);
      setReplyingTo(null);
    } catch (_) {
      setError(t("failedToSendMessage"));
    }
  };

  const handleReply = useCallback((msg: ChatMessage) => {
    setReplyingTo(msg);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const cancelReply = () => setReplyingTo(null);

  const scrollToMessage = useCallback((id: number) => {
    const el = messagesContainerRef.current?.querySelector<HTMLElement>(`[data-msg-id="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("chat-message-flash");
      setTimeout(() => el.classList.remove("chat-message-flash"), 1600);
    }
  }, []);

  const startEdit = (msg: ChatMessage) => {
    setEditingId(msg.id);
    setEditingText(msg.content);
    // Prime mentions with the ones currently on the message, so filtering-by-text on save still works.
    setEditingMentions((msg.mentions ?? []).map(m => ({ playerId: m.playerId, name: m.name })));
    requestAnimationFrame(() => editInputRef.current?.focus());
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
    setEditingMentions([]);
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    const trimmed = editingText.trim();
    if (!trimmed) return;
    const mentionedPlayerIds = editingMentions
      .filter(m => editingText.includes(`@${m.name}`))
      .map(m => m.playerId);
    try {
      await editMutation.mutateAsync({ id: editingId, content: editingText, mentionedPlayerIds });
      cancelEdit();
    } catch (_) {
      setError(t("failedToEditMessage"));
    }
  };

  const handleAddReaction = async (id: number, emoji: string) => {
    try {
      await addReactionMutation.mutateAsync({ messageId: id, emoji });
    } catch (_) {
      setError(t("failedToReactToMessage"));
    }
  };

  const handleRemoveReaction = async (id: number, emoji: string) => {
    try {
      await removeReactionMutation.mutateAsync({ messageId: id, emoji });
    } catch (_) {
      setError(t("failedToReactToMessage"));
    }
  };

  const handleDelete = async () => {
    if (messageToDelete == null) return;
    try {
      await deleteMutation.mutateAsync(messageToDelete);
    } catch (_) {
      setError(t("failedToDeleteMessage"));
    } finally {
      setShowDeleteMessageConfirmModal(false);
      setMessageToDelete(null);
    }
  };

  const showDeleteConfirmModal = (id: number) => {
    setMessageToDelete(id);
    setShowDeleteMessageConfirmModal(true);
  };

  const handleTogglePin = async (msg: ChatMessage) => {
    try {
      await pinMutation.mutateAsync({ id: msg.id, pinned: !msg.pinned });
    } catch (_) {
      setError(t("failedToPinMessage"));
    }
  };

  const mentionsCurrentPlayer = useCallback((msg: ChatMessage): boolean => {
    if (currentPlayerId == null) return false;
    return (msg.mentions ?? []).some(m => m.playerId === currentPlayerId);
  }, [currentPlayerId]);

  const canEdit = useCallback((msg: ChatMessage): boolean => {
    if (msg.author.userId !== currentUser) return false;
    return nowTick - new Date(msg.createdAt).getTime() < EDIT_WINDOW_MS;
  }, [currentUser, nowTick]);

  const typingLabel = useMemo(() => {
    if (typingNames.length === 0) return null;
    if (typingNames.length === 1) return t("someoneIsTyping", { name: typingNames[0] });
    if (typingNames.length === 2) return t("twoAreTyping", { first: typingNames[0], second: typingNames[1] });
    return t("severalAreTyping", { count: typingNames.length });
  }, [typingNames, t]);

  const renderMessage = (msg: ChatMessage, index: number) => {
    const isSystem = !!msg.system;
    const userColor = isSystem ? "var(--chat-accent, var(--accent))" : getUserColor(msg.author.userId);
    const isOwn = !isSystem && msg.author.userId === currentUser;
    const isMentioned = mentionsCurrentPlayer(msg);
    const isEditing = editingId === msg.id;
    const showEdit = !isSystem && canEdit(msg) && !isEditing;
    return (
      <motion.div
        className={`chat-message${isOwn ? " own" : ""}${isMentioned ? " mentioned" : ""}${msg.pinned ? " pinned" : ""}${isSystem ? " system" : ""}`}
        key={msg.id}
        data-msg-id={msg.id}
        initial={{ opacity: 0, y: 15, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95, x: isOwn ? 20 : -20 }}
        transition={{ duration: 0.25, delay: index < 10 ? index * 0.03 : 0 }}
        layout
        style={isSystem ? undefined : { border: `2px solid ${userColor}` }}
      >
        {msg.pinned && (
          <div className="chat-message-pinned-badge" title={t("pinned")}>
            📌 {t("pinned")}
          </div>
        )}
        {msg.replyTo && (
          <div
            className="chat-reply-context clickable"
            onClick={() => msg.replyTo && scrollToMessage(msg.replyTo.id)}
          >
            <span className="chat-reply-arrow" aria-hidden="true">↩</span>
            <span className="chat-reply-author">{msg.replyTo.authorName}</span>
            <span className="chat-reply-snippet">{msg.replyTo.contentPreview}</span>
          </div>
        )}
        <div className="chat-message-header">
          {isSystem ? (
            <span className="chat-author chat-author-system">🤖 Hravý Bot</span>
          ) : (
            <span
              className="chat-author clickable"
              style={{ color: userColor }}
              onClick={() => linkToPlayer(msg.author.id)}
            >
              {msg.authorName}
            </span>
          )}
          <span className="chat-date">
            {formatDate(msg.createdAt)}, {formatTime(msg.createdAt)}
            {msg.editedAt && <span className="chat-edited-marker" title={t("editedAt", { time: formatTime(msg.editedAt) })}> ({t("edited")})</span>}
          </span>
        </div>
        {isEditing ? (
          <div className="chat-edit-wrapper">
            <input
              ref={editInputRef}
              className="chat-input chat-edit-input"
              type="text"
              value={editingText}
              onChange={editAutocomplete.handleChange}
              onKeyDown={(e) => {
                editAutocomplete.handleKeyDown(e);
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  saveEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
            />
            {editAutocomplete.overlay}
            <div className="chat-edit-actions">
              <button className="chat-edit-cancel" type="button" onClick={cancelEdit}>{t("cancel")}</button>
              <button className="chat-edit-save" type="button" onClick={saveEdit} disabled={editMutation.isPending}>
                {t("save")}
              </button>
            </div>
          </div>
        ) : (
          <div className="chat-content">
            <ChatMessageBody content={msg.content} mentions={msg.mentions ?? []} />
          </div>
        )}
        <div className="chat-actions">
          <ChatMessageReactions
            reactions={msg.reactions ?? []}
            canReact={!!currentUser}
            onAddReaction={(emoji) => handleAddReaction(msg.id, emoji)}
            onRemoveReaction={(emoji) => handleRemoveReaction(msg.id, emoji)}
          />
          {canSend && !isEditing && (
            <button
              className="chat-icon-btn"
              onClick={() => handleReply(msg)}
              aria-label={t("reply")}
              title={t("reply")}
              type="button"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="9 17 4 12 9 7" />
                <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
              </svg>
            </button>
          )}
          {showEdit && (
            <button
              className="chat-icon-btn"
              onClick={() => startEdit(msg)}
              aria-label={t("edit")}
              title={t("edit")}
              type="button"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </button>
          )}
          {isAdmin() && !isEditing && (
            <button
              className="chat-icon-btn"
              onClick={() => handleTogglePin(msg)}
              aria-label={msg.pinned ? t("unpin") : t("pin")}
              title={msg.pinned ? t("unpin") : t("pin")}
              type="button"
            >
              <span aria-hidden="true">📌</span>
            </button>
          )}
          {(isAdmin() || msg.author.userId === currentUser) && !isEditing && (
            <button
              className="chat-delete-icon-btn ms-auto"
              onClick={() => showDeleteConfirmModal(msg.id)}
              aria-label={t("delete")}
              title={t("delete")}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className={`${asPage ? "base-container " : ""}chat-container`}>
      {showHeader && (
        <div className="table-header header">
          <span className="table-title">{t("chat")}</span>
        </div>
      )}
      <Alert show={showWarning} variant="info" className="m-3">
        <Alert.Heading>{t("wannaChat")}</Alert.Heading>
        <p>{t("chatRules1")}</p>
        <hr />
        <div className="d-flex justify-content-end">
          <Button onClick={() => closeWarning()} variant="outline-info">
            {t("iUnderstand")}
          </Button>
        </div>
      </Alert>
      {canSend ? (
        <>
          {replyingTo && (
            <div className="chat-reply-preview">
              <div className="chat-reply-preview-body">
                <span className="chat-reply-arrow" aria-hidden="true">↩</span>
                <span className="chat-reply-preview-label">{t("replyingTo")} </span>
                <span className="chat-reply-preview-author">{messageAuthorDisplayName(replyingTo)}</span>
                <span className="chat-reply-preview-snippet">: {replyingTo.content.slice(0, 100)}{replyingTo.content.length > 100 ? "…" : ""}</span>
              </div>
              <button className="chat-reply-preview-cancel" type="button" onClick={cancelReply} aria-label={t("cancel")}>×</button>
            </div>
          )}
          <form className="chat-input-form" onSubmit={handleSend}>
            <div className="chat-input-wrapper">
              <input
                ref={inputRef}
                className="chat-input"
                type="text"
                value={newMessage}
                onChange={(e) => { onInputChange(e); notifyTyping(); }}
                onKeyDown={onInputKeyDown}
                placeholder={t("typeMessage")}
                disabled={sending}
                max="1000"
              />
              {mentionOverlay}
            </div>
            <div className="chat-emoji-wrapper" ref={emojiPickerRef}>
              <button
                className="chat-emoji-btn"
                type="button"
                onClick={() => setShowEmojiPicker(v => !v)}
                disabled={sending}
                title={t("emoji")}
                aria-label={t("emoji")}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              </button>
              {showEmojiPicker && (
                <div className="chat-emoji-popover">
                  <EmojiPicker
                    theme={theme === "light" ? EmojiTheme.LIGHT : EmojiTheme.DARK}
                    onEmojiClick={(emojiObj) => setNewMessage(prev => prev + emojiObj.emoji)}
                    width={isMobile ? 280 : 320}
                    height={400}
                    lazyLoadEmojis
                    categories={i18n.language === "cs" ? CZ_EMOJI_CATEGORIES : undefined}
                    searchPlaceHolder={i18n.language === "cs" ? "Hledat…" : "Search…"}
                    previewConfig={{
                      defaultCaption: i18n.language === "cs" ? "Jak se dnes cítíš?" : "What's your mood?",
                      defaultEmoji: "1f600",
                      showPreview: true,
                    }}
                  />
                </div>
              )}
            </div>
            <ChatGifPicker
              onSelect={(url) => setNewMessage(prev => (prev.length === 0 || prev.endsWith(" ") ? prev : prev + " ") + url + " ")}
            />
            <button className="chat-send-btn" type="submit" disabled={sending || !newMessage.trim()}>
              <span>{t("send")}</span>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 11L21 3l-8 18-2-8z" />
              </svg>
            </button>
          </form>
          {typingLabel && <div className="chat-typing-indicator">{typingLabel}</div>}
        </>
      ) : (
        <Alert variant="primary" className="ms-3 me-3">
          {t("cannotChatInfo")}
        </Alert>
      )}
      {pinnedMessages.length > 0 && (
        <div className="chat-pinned-section">
          <button
            className="chat-pinned-toggle"
            type="button"
            onClick={() => setPinnedCollapsed(v => !v)}
          >
            <span aria-hidden="true">📌</span> {t("pinned")} ({pinnedMessages.length})
            <span className="chat-pinned-toggle-chevron">{pinnedCollapsed ? "▸" : "▾"}</span>
          </button>
          {!pinnedCollapsed && (
            <div className="chat-pinned-list">
              {pinnedMessages.map((p) => (
                <div
                  key={p.id}
                  className="chat-pinned-item clickable"
                  onClick={() => scrollToMessage(p.id)}
                >
                  <span className="chat-pinned-item-author" style={{ color: getUserColor(p.author.userId) }}>
                    {messageAuthorDisplayName(p)}
                  </span>
                  <span className="chat-pinned-item-snippet">: {p.content.slice(0, 120)}{p.content.length > 120 ? "…" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="chat-messages" ref={messagesContainerRef}>
        {loading && <div className="chat-loading">{t("loading")}</div>}
        <AnimatePresence mode="popLayout">
          {messages.map((msg, index) => renderMessage(msg, index))}
        </AnimatePresence>
        {hasNextPage && (
          <motion.button
            className="chat-load-more"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            whileTap={{ scale: 0.97 }}
          >
            {isFetchingNextPage ? t("loading") : t("loadMore")}
          </motion.button>
        )}
      </div>
      {error && <div className="chat-error">{error}</div>}
      {showDeleteMessageConfirmModal ?
        <ConfirmModal title={t("deleteMessage")} message={t("deleteMessageConfirmation")}
                      onCancel={() => setShowDeleteMessageConfirmModal(false)}
                      onConfirm={() => handleDelete()}/>
        : null}
    </div>
  );
}

export default ChatPanel;
